import { ApplicationFormPageEnum } from '../types';
import { useMutation } from '@tanstack/react-query';
import { applicationFormApi } from '@api/applicationForm/applicationFormApi';
import { Button } from '@components/common/Button';
import { FormCheckbox } from '@components/common/FormCheckbox/FormCheckbox';
import { FormControlLabel } from '@components/common/FormControlLabel';
import { FormGroup } from '@components/common/FormGroup';
import { LoadingButton } from '@components/common/LoadingButton/LoadingButton';
import { useNavigateBack } from '@hooks/useNavigateBack';
import { useApplicationInProgressById } from '@queries/applications/useApplicationInProgressById';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { PageURL } from '@router/pageURL';
import { getCurrentRoute } from '@utils/location';
import { parseApplicationInProgress } from '../helpers';
import { reviewAndSubmitQuestions } from './questions';
import { CustomerDeclaration } from '@components/CustomerDeclaration/CustomerDeclaration';
import { IReviewAndSubmitForm } from './types';
import { useSnackbar } from 'notistack';

export const ReviewAndSubmit = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { enqueueSnackbar } = useSnackbar();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { mutate: postApplicationInProgress, isLoading } = useMutation(applicationFormApi.postApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);

  const form = useForm<IReviewAndSubmitForm>({
    defaultValues: {
      termsConfirmed: false,
      privacyAccepted: false,
    },
  });

  const formState = form.watch();

  const isNextStepDisabled = !formState.termsConfirmed || !formState.privacyAccepted;

  const submitFormHandler = async (formData: IReviewAndSubmitForm) => {
    const { termsConfirmed, privacyAccepted } = formData;

    if (termsConfirmed && privacyAccepted) {
      const questions = Object.entries(formData).map(([key, answer]) => ({
        question: intl.formatMessage({ id: reviewAndSubmitQuestions[key].labelKey }),
        key,
        answer,
      }));

      await patchApplicationInProgress({ questions, currentRoute, applicationId });

      await postApplicationInProgress(applicationId, {
        onSuccess: () => {
          searchParams.set('section', ApplicationFormPageEnum.IN_REVIEW);
          setSearchParams(searchParams);
        },
        onError: () => {
          // Force down a path to review everything
          enqueueSnackbar('Please review all the fields and try again', { variant: 'error' });
          searchParams.set('section', ApplicationFormPageEnum.CRITERIA);
          setSearchParams(searchParams);
        },
      });
    }
  };

  useEffect(() => {
    if (applicationInProgress?.[currentRoute]) {
      const { termsConfirmed, privacyAccepted } = parseApplicationInProgress(applicationInProgress[currentRoute]);

      form.reset({
        termsConfirmed,
        privacyAccepted,
      });
    }
  }, [applicationInProgress]);

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)} className={'h-vh65'}>
        <CustomerDeclaration className={'h-full overflow-scroll'} />

        <FormGroup>
          <FormControlLabel
            control={<FormCheckbox name={'termsConfirmed'} />}
            label={intl.formatMessage({ id: reviewAndSubmitQuestions.termsConfirmed.labelKey })}
          />
          <FormControlLabel
            control={<FormCheckbox name={'privacyAccepted'} />}
            label={
              <FormattedMessage
                id={'applicationForm.REVIEW_AND_SUBMIT.privacyAccepted'}
                values={{
                  a: text => (
                    <a target={'_blank'} rel={'noreferrer'} href={PageURL.PRIVACY_POLICY}>
                      {text}
                    </a>
                  ),
                }}
              />
            }
          />
        </FormGroup>

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <LoadingButton
            isLoading={isLoading}
            buttonProps={{ variant: 'contained', type: 'submit', disabled: isNextStepDisabled }}
          >
            <FormattedMessage id={'cta.continue'} />
          </LoadingButton>
        </div>
      </form>
    </FormProvider>
  );
};
